import { campaignDetailsData_mock, impactStatsData_mock, marketStatsData_mock } from 'data/campaignDetailsData';
import { CampaignDetails, CampaignMetadata } from 'modules/Types';
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import useCurrentBrand, { BrandExistsState } from './useCurrentBrand';
import {
    getPresaveCampaignPresaveStatus_api,
    retryApplyPresaves_api,
    getCampaignAnalyticsV2_api,
    getCampaignAnalytics_impactStats_v2_api,
    CampaignImpactStatsMetric,
    CampaignDetailsData,
    updateCampaignPauseStatus_api, duplicateCampaign_api, getCampaign_api, downloadCampaignReport_api,
    PresaveCampaignPresaveStatus,
    getPresaveCampaignAnalytics_api,
    adminRetryCampaign_api,
    renameCampaign_api,
    updateCampaignBudget_api,
    updateCampaignEndDate_api
} from 'api/campaignApi';

interface CampaignDetailsState {
    // State
    campaign: CampaignDetails | null;
    campaignId: number | null;
    associatedContentSlug: string | null;
    campaignAnalyticsData: CampaignDetailsData | null;
    marketStatsData: any | null;
    impactStatsData: CampaignImpactStatsMetric[] | null;
    isLoadingCampaign: boolean;
    isLoadingCampaignAnalytics: boolean;
    isLoadingMarketStatsData: boolean;
    isLoadingImpactData: boolean;
    isRetryingApplyPresaves: boolean;

    showEditBudgetModal: boolean;
    showEditEndDateModal: boolean;

    isPausingResumingCampaign: boolean;
    isDuplicatingCampaign: boolean;
    isRenamingCampaign: boolean;
    isEditingCampaignEndDate: boolean;
    isEditingCampaignBudget: boolean;
    isArchivingCampaign: boolean;
    isRetryingCampaign: boolean;
    isDownloadingReport: boolean;

    // Setters
    setCampaignId: (campaignId: number) => void;
    setCampaignDetails: (details: any) => void;
    setMarketStatsData: (data: any) => void;
    setIsLoadingCampaignDetails: (loading: boolean) => void;
    setIsLoadingMarketStatsData: (loading: boolean) => void;
    setIsLoadingImpactData: (loading: boolean) => void;
    setShowEditBudgetModal: (show: boolean) => void;
    setShowEditEndDateModal: (show: boolean) => void;

    // Methods
    getPresaveCampaignPresaveStatus: (campaignId: number) => Promise<PresaveCampaignPresaveStatus>;
    retryApplyPresaves: (campaignId: number) => Promise<any>;
    getPresaveCampaignAnalytics: (campaignId: number) => Promise<any>;
    getCampaign: (campaignId: number) => Promise<CampaignDetails | null | undefined>
    getCampaignAnalytics: (campaignId: number) => Promise<any>;
    getMarketStatsData: (campaignId: number) => Promise<any>;
    getImpactData: (campaignId: number) => Promise<any>;

    updateCampaignMetadata: (data: CampaignMetadata) => void;

    // actions on campaign
    pauseCampaign: (campaignId: number) => Promise<CampaignDetails>;
    resumeCampaign: (campaignId: number) => Promise<CampaignDetails>;
    duplicateCampaign: (campaignId: number) => Promise<any>;
    renameCampaign: (campaignId: number, newName: string) => Promise<string | undefined>;
    editBudget: (campaignId: number, newBudget: number) => Promise<any>;
    editEndDate: (campaignId: number, newDate: string) => Promise<any>;
    archiveCampaign: (campaignId: number) => Promise<any>;
    resetCampaignDetails: () => void;
    adminRetryCampaign: (campaignId: number, socialPlatform?: 'facebook' | 'tiktok') => Promise<any>;
    downloadCampaignReport: (campaignId: number) => Promise<any>;
}

export interface CampaignExistsState extends CampaignDetailsState {
    campaignId: number;
    campaign: CampaignDetails | any | null;
}

const useCampaignDetails = create<CampaignDetailsState>()(
    immer<CampaignDetailsState>((set, get) => ({
        // State
        campaignId: null,
        campaign: null,
        associatedContentSlug: null,
        campaignAnalyticsData: null,
        marketStatsData: null,
        impactStatsData: null,
        isLoadingCampaign: true,
        isLoadingCampaignAnalytics: true,
        isLoadingMarketStatsData: true,
        isLoadingImpactData: true,
        isPausingResumingCampaign: false,
        isDuplicatingCampaign: false,
        isRenamingCampaign: false,
        isEditingCampaignEndDate: false,
        isEditingCampaignBudget: false,
        isDownloadingReport: false,
        isRetryingApplyPresaves: false,
        isArchivingCampaign: false,
        isRetryingCampaign: false,

        showEditBudgetModal: false,
        showEditEndDateModal: false,

        // Setters
        updateCampaignMetadata: (data: CampaignMetadata) => {
            set((state) => {
                if (state.campaign) {
                    state.campaign.campaign_metadata = {
                        ...state.campaign.campaign_metadata,
                        ...data
                    }
                }
            })
        },
        setCampaignId: (campaignId: number) => { set((state) => { state.campaignId = campaignId }) },
        setCampaignDetails: (details) => { set((state) => { state.campaignAnalyticsData = details }) },
        setMarketStatsData: (data) => { set((state) => { state.marketStatsData = data }) },
        setIsLoadingCampaignDetails: (loading) => { set((state) => { state.isLoadingCampaignAnalytics = loading }) },
        setIsLoadingMarketStatsData: (loading) => { set((state) => { state.isLoadingMarketStatsData = loading }) },
        setIsLoadingImpactData: (loading) => { set((state) => { state.isLoadingImpactData = loading }) },
        setShowEditBudgetModal: (show: boolean) => { set((state) => { state.showEditBudgetModal = show }) },
        setShowEditEndDateModal: (show: boolean) => { set((state) => { state.showEditEndDateModal = show }) },

        getPresaveCampaignPresaveStatus: async (campaignId: number): Promise<PresaveCampaignPresaveStatus> => {
            const response = await getPresaveCampaignPresaveStatus_api({ campaignId })
            return response
        },

        retryApplyPresaves: async (campaignId: number): Promise<any> => {
            try {
                set((state) => { state.isRetryingApplyPresaves = true })
                const response = await retryApplyPresaves_api({ campaignId })
                return response
            } catch (error) {
                console.error('Error retrying apply presaves:', error);
                throw error;
            } finally {
                set((state) => { state.isRetryingApplyPresaves = false })
            }
        },

        getPresaveCampaignAnalytics: async (campaignId: number) => {
            const response = await getPresaveCampaignAnalytics_api({ campaignId })
            return response
        },

        getCampaign: async (campaignId: number): Promise<CampaignDetails | null | undefined> => {
            set((state) => {
                state.campaignId = campaignId
                state.isLoadingCampaign = true
            })

            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState
            const currentBrandAccessToken = currentBrand?.connections?.logged_in_fb_user?.access_token

            try {
                let campaignData = await getCampaign_api(campaignId)
                if (!campaignData) {
                    throw new Error('useCampaignDetails::getCampaignDetails:: No campaign data found')
                }

                /** Validate if user has access to this campaign */
                const campaignBrandId = campaignData.brand_id
                if (campaignBrandId !== currentBrand?.id) {
                    // handleGoBackCampaignDetails({ history })
                }

                /** Check if campaign is a Facebook campaign type */
                // const isFbCampaign = PLAYBOOK_TYPE_INFO[
                //     campaignData.campaign_metadata.campaign_type as CampaignType
                // ]?.adPlatform === AdPlatform.META
                /** Check if campaign is a pre-save type */
                const isPreSave = campaignData.campaign_metadata.campaign_type === 'pre_save'

                set((state) => {
                    state.campaign = campaignData
                })

                return campaignData
            } catch (err) {
                set((state) => {
                    // state.error = err instanceof Error ? err.message : 'An error occurred'
                })
            } finally {
                set((state) => {
                    state.isLoadingCampaign = false
                })
            }
        },

        // Methods
        getCampaignAnalytics: async (campaignId: number) => {
            try {
                let MOCK_DATA = false;

                set((state) => { state.isLoadingCampaignAnalytics = true })
                if (MOCK_DATA) {
                    const campaignDetailsDataLocal = campaignDetailsData_mock;
                    set((state) => {
                        state.campaignAnalyticsData = campaignDetailsDataLocal as unknown as CampaignDetailsData
                        state.associatedContentSlug = 'test-song'
                    })
                    return campaignDetailsDataLocal;
                } else {
                    const campaignDetailsDataLocal = await getCampaignAnalyticsV2_api({ campaignId });
                    set((state) => {
                        state.campaignAnalyticsData = campaignDetailsDataLocal || null
                        state.associatedContentSlug = 'test-song'
                    })
                    return campaignDetailsDataLocal;
                }
            } catch (error) {
                console.error('Error fetching campaign details:', error);
            } finally {
                set((state) => {
                    state.isLoadingCampaignAnalytics = false
                })
            }
        },
        getMarketStatsData: async (campaignId: number) => {
            try {
                set((state) => { state.isLoadingMarketStatsData = true })
                const marketStatsDataLocal = marketStatsData_mock;
                set((state) => {
                    state.marketStatsData = marketStatsDataLocal
                })
                return marketStatsDataLocal;
            } catch (error) {
                console.error('Error fetching market stats:', error);
            } finally {
                set((state) => { state.isLoadingMarketStatsData = false })
            }
        },

        ////// MEGH STUB
        getImpactData: async (campaignId: number) => {
            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState
            const {
                associatedContentSlug: contentSlug
            } = get()

            let MOCK_DATA = false;

            try {
                set((state) => { state.isLoadingImpactData = true })
                if (MOCK_DATA) {
                    const impactStatsDataLocal = impactStatsData_mock;
                    set((state) => {
                        state.impactStatsData = impactStatsDataLocal
                    })
                    return impactStatsDataLocal;
                } else {
                    const impactStatsDataLocal = await getCampaignAnalytics_impactStats_v2_api({ campaignId });
                    set((state) => {
                        state.impactStatsData = impactStatsDataLocal || []
                    })
                    return impactStatsDataLocal || [];
                }
            } catch (error) {
                console.error('Error fetching campaign details:', error);
            } finally {
                set((state) => {
                    state.isLoadingImpactData = false
                })
            }

            return;
        },

        pauseCampaign: async (campaignId: number): Promise<CampaignDetails> => {
            try {
                set((state) => { state.isPausingResumingCampaign = true })
                const response = await updateCampaignPauseStatus_api({
                    campaignId,
                    pause: true
                });
                set((state) => {
                    state.campaign = response
                })
                return response;
            } catch (error) {
                console.error('Error pausing campaign:', error);
                throw error;
            } finally {
                set((state) => { state.isPausingResumingCampaign = false })
            }
        },

        resumeCampaign: async (campaignId: number): Promise<CampaignDetails> => {
            try {
                set((state) => { state.isPausingResumingCampaign = true })
                const response = await updateCampaignPauseStatus_api({
                    campaignId,
                    pause: false
                });
                set((state) => {
                    state.campaign = response
                })
                return response;
            } catch (error) {
                console.error('Error pausing campaign:', error);
                throw error;
            } finally {
                set((state) => { state.isPausingResumingCampaign = false })
            }
        },

        duplicateCampaign: async (campaignId) => {
            try {
                set((state) => { state.isDuplicatingCampaign = true })
                console.log('API call - duplicateCampaign_api with ID:', campaignId);
                const res = await duplicateCampaign_api({ campaignId });
                console.log('Raw API response from duplicateCampaign_api:', res);

                // Check various possible response structures
                if (res) {
                    console.log('Response has these keys:', Object.keys(res));
                    if (res.data) {
                        console.log('Response.data has these keys:', Object.keys(res.data));
                    }
                    if (res.campaign) {
                        console.log('Response.campaign has these keys:', Object.keys(res.campaign));
                    }
                }

                return res;
            } catch (error) {
                console.error('Error duplicating campaign:', error);
                throw error;
            } finally {
                set((state) => { state.isDuplicatingCampaign = false })
            }
        },

        renameCampaign: async (campaignId, newName) => {
            try {
                set((state) => { state.isRenamingCampaign = true });
                const res = await renameCampaign_api({ campaignId, newName });
                return res.data;
            } catch (error) {
                console.error('Error renaming campaign:', error);
            } finally {
                set((state) => { state.isRenamingCampaign = false });
            }
        },

        editBudget: async (campaignId, newBudget) => {
            try {
                set((state) => { state.isEditingCampaignBudget = true });
                const res = await updateCampaignBudget_api({ campaignId, budget: newBudget });
                return res.data;
            } catch (error) {
                console.error('Error editing budget: ', error);
                throw error;
            } finally {
                set((state) => { state.isEditingCampaignBudget = false });
            }
        },

        editEndDate: async (campaignId, newDate) => {
            try {
                set((state) => { state.isEditingCampaignEndDate = true });
                const res = await updateCampaignEndDate_api({ campaignId, endDate: newDate });
                return res.data;
            } catch (error) {
                console.error('Error editing endDate: ', error);
                throw error;
            } finally {
                set((state) => { state.isEditingCampaignEndDate = false });
            }
        },

        archiveCampaign: async (campaignId) => {
            try {
                // set((state) => { state.isArchivingCampaign = true })
                // console.log('API call - archiveCampaign_api with ID:', campaignId);
                // const response = await archiveCampaign_api({ campaignId });
                // console.log('Raw API response from archiveCampaign_api:', response);

                // // Check various possible response structures
                // if (response) {
                //     console.log('Response has these keys:', Object.keys(response));
                //     if (response.data) {
                //         console.log('Response.data has these keys:', Object.keys(response.data));
                //     }
                //     if (response.campaign) {
                //         console.log('Response.campaign has these keys:', Object.keys(response.campaign));
                //     }
                // }

                // return response;
                return true;
            } catch (error) {
                console.error('Error duplicating campaign:', error);
                throw error; // Re-throw the error to be caught by the caller
            } finally {
                set((state) => { state.isArchivingCampaign = false })
            }
        },

        resetCampaignDetails: () => {
            const {
                isLoadingCampaign,
            } = get()

            if (isLoadingCampaign) {
                return;
            }

            set((state) => {
                state.campaign = null
                state.campaignId = null
                state.campaignAnalyticsData = null
                state.marketStatsData = null
                state.impactStatsData = null
                state.isLoadingCampaign = true
                state.isLoadingCampaignAnalytics = true
                state.isLoadingMarketStatsData = true
                state.isLoadingImpactData = true
            })
        },

        adminRetryCampaign: async (campaignId: number, socialPlatform?: 'facebook' | 'tiktok') => {
            try {
                set((state) => { state.isRetryingCampaign = true })
                const response = await adminRetryCampaign_api({ campaignId, socialPlatform });
                return response;
            } catch (error) {
                console.error('Error retrying campaign:', error);
            } finally {
                set((state) => { state.isRetryingCampaign = false })
            }
        },

        downloadCampaignReport: async (campaignId: number) => {
            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState

            try {
                set((state) => { state.isDownloadingReport = true })
                const currentBrandId = currentBrand?.id
                const response = await downloadCampaignReport_api({ campaignId, brandId: currentBrandId });
                return response;
            } catch (error) {
                console.error('Error downloading campaign report:', error);
            } finally {
                set((state) => { state.isDownloadingReport = false })
            }
        }
    }))
)

export default useCampaignDetails;